import config from '@/config'
import { createAuthenticatedFetch } from '@/helpers/api/httpClient'

const apiFetch = createAuthenticatedFetch()
const base = () => `${config.API_URL}/api/notification`
export const NOTIFICATION_UNREAD_COUNT_CHANGED = 'notification-unread-count-changed'

async function parseJson<T>(res: Response): Promise<T> {
	if (!res.ok) {
		const err = await res.json().catch(() => ({}))
		return Promise.reject(err)
	}
	return res.json() as Promise<T>
}

export interface TenantNotification {
	id: number
	title: string
	message: string
	isRead: boolean
	icon: string
	iconColor: string
	notificationType: number
	priority: number
	actionUrl: string | null
	createdOn: string
}

export interface NotificationsPage {
	data: TenantNotification[]
	totalCount: number
	currentPage: number
	totalPages: number
	pageSize: number
}

export interface GetNotificationsRequest {
	pageNumber: number
	pageSize: number
	isRead?: boolean | null
}

const SUPER_ADMIN_ICON_BY_TYPE: Record<number, { icon: string; iconColor: string }> = {
	0: { icon: 'ri-user-add-line', iconColor: 'text-info' },
	1: { icon: 'ri-flask-line', iconColor: 'text-primary' },
	2: { icon: 'ri-shopping-cart-line', iconColor: 'text-success' },
	3: { icon: 'ri-arrow-up-circle-line', iconColor: 'text-success' },
	4: { icon: 'ri-error-warning-line', iconColor: 'text-danger' },
	5: { icon: 'ri-alarm-warning-line', iconColor: 'text-warning' },
	6: { icon: 'ri-close-circle-line', iconColor: 'text-danger' },
	7: { icon: 'ri-key-2-line', iconColor: 'text-primary' },
}

/** Normalize SignalR payloads (tenant + legacy super-admin shapes) for UI components. */
export function normalizeNotification(msg: Record<string, unknown> | null | undefined): TenantNotification | null {
	if (!msg || typeof msg !== 'object') return null

	const id = (msg.id ?? msg.Id) as number | undefined
	if (id == null) return null

	const notificationType = Number(msg.notificationType ?? msg.NotificationType ?? 0)
	const priority = Number(msg.priority ?? msg.Priority ?? 1)
	const fallbackIcon = SUPER_ADMIN_ICON_BY_TYPE[notificationType] ?? {
		icon: 'ri-notification-3-line',
		iconColor: 'text-primary',
	}

	return {
		id,
		title: String(msg.title ?? msg.Title ?? ''),
		message: String(msg.message ?? msg.Message ?? ''),
		isRead: Boolean(msg.isRead ?? msg.IsRead ?? false),
		icon: String(msg.icon ?? msg.Icon ?? fallbackIcon.icon),
		iconColor: String(msg.iconColor ?? msg.IconColor ?? fallbackIcon.iconColor),
		notificationType,
		priority,
		actionUrl: (msg.actionUrl ?? msg.ActionUrl ?? null) as string | null,
		createdOn: String(msg.createdOn ?? msg.CreatedOn ?? new Date().toISOString()),
	}
}

export function notifyUnreadCountChanged(count?: number) {
	if (typeof window === 'undefined') return
	window.dispatchEvent(new CustomEvent<number | undefined>(NOTIFICATION_UNREAD_COUNT_CHANGED, { detail: count }))
}

export const notificationsApi = {
	list: async (req: GetNotificationsRequest): Promise<NotificationsPage> => {
		const res = await apiFetch(`${base()}/list`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(req),
		})
		return parseJson<NotificationsPage>(res)
	},

	unreadCount: async (): Promise<number> => {
		const res = await apiFetch(`${base()}/unread-count`)
		return parseJson<number>(res)
	},

	markRead: async (id: number): Promise<void> => {
		await apiFetch(`${base()}/${id}/mark-read`, { method: 'POST' })
	},

	markAllRead: async (): Promise<void> => {
		await apiFetch(`${base()}/mark-all-read`, { method: 'POST' })
	},

	delete: async (id: number): Promise<void> => {
		await apiFetch(`${base()}/${id}`, { method: 'DELETE' })
	},
}
